import TableContext from 'config/context/tableContext';
import React, { createContext, useContext, useMemo, useState } from 'react';

const MfImportContext = createContext();

export const MfImportProvider = ({ children }) => {
    const { tableStructures } = useContext(TableContext);
    const [step, setStep] = useState(1);
    const [pkey, setPkey] = useState(null);
    const [file, setFile] = useState(null);
    // Izabrani plugin iz kataloga i njegova konfiguracija. Fajl izvori popune
    // `file`, konekcioni `config` — ekstenzija više nije ono što bira rutu.
    const [plugin, setPlugin] = useState(null);
    const [config, setConfig] = useState({});
    const [selectedPermission, setSelectedPermission] = useState('active');
    const [exist, setExist] = useState(true);
    const [selectedComposition, setSelectedComposition] = useState('');
    const [structs, setStructs] = useState([]);
    // Ime izvedeno iz izabranog cilja u izvoru (MySQL tabela, Mongo kolekcija,
    // Redis obrazac), za slučaj JEDNOG cilja. Prazno je kad ga nema odakle
    // izvesti (fajl izvori) ili kad je takvo ime u bazi već zauzeto — tada ga
    // ne vredi nuditi, jer uvoz ne može da menja postojeću kompoziciju.
    //
    // Drži se odvojeno od `selectedComposition` zato što prelazak na jezičak
    // „Create new composition" mora da obriše ime izabrano na jezičku „Use
    // existing" — te dve vrednosti dele isto polje, a znače različite stvari.
    // Bez zasebnog izvora predlog bi se pri tom brisanju izgubio.
    const [derivedName, setDerivedName] = useState('');
    const [table, setTable] = useState({ fields: [], indexes: [], description: '' });
    // Jedan prolaz ume da nosi VIŠE kompozicija — više tabela iz MySQL baze,
    // kolekcija iz Mongo-a, obrazaca ključeva iz Redis-a. `table` je ona koja
    // se TRENUTNO pregleda, `tables` su sve.
    //
    // Zašto oba, a ne samo niz: korak 2 (`MfCreateNewComp`) radi nad jednom
    // strukturom i tako je i napisan. Držanjem tekuće u `table` on ostaje
    // nedirnut, a jezičci samo prebacuju koja je tekuća.
    const [tables, setTables] = useState([]);
    const [activeTable, setActiveTable] = useState(0);
    // Šta se pri prelasku gubi ili slabi. Puni ga korak 1 (parseMf), a čita
    // korak 2, pa mora ovde — hook-ovo stanje ne prelazi između koraka.
    const [degradations, setDegradations] = useState([]);
    // Šta je od ciljeva već na lancu (redovi, dopisuje/zamenjuje, nesklad).
    // Puni ga korak 1 iz `/structure`; korak 2 ga prikazuje.
    const [existing, setExisting] = useState([]);
    const [instance, setInstance] = useState('');

    const allCompositionsList = useMemo(() =>
        new Set((tableStructures || []).map(({ type }) => type?.toLowerCase()).filter(Boolean)),
        [tableStructures]
    );

    // Konekcioni izvori nemaju fajl, pa ovo mora da trpi `null` — inače korak 2
    // pukne na `file.name` čim se uveze iz MySQL-a ili Mongo-a.
    const fileType = () => String(file?.name || '').split('.').pop().toLowerCase();

    /**
     * Prelazak na drugu tabelu u istom uvozu.
     *
     * Tekuće izmene se PRVO upisuju natrag u spisak. Bez toga bi promena
     * jezička tiho odbacila štiklirane indekse — a to je izbor koji korisnik
     * posle ne bi mogao da povrati, jer se šema na lancu ne menja.
     */
    const selectTable = (next) => {
        if (next === activeTable || !tables[next]) return;
        const merged = tables.map((t, i) => (i === activeTable ? { ...t, ...table } : t));
        setTables(merged);
        setTable(merged[next]);
        setActiveTable(next);
    };

    /** Spisak sa upisanim tekućim izmenama — ono što se stvarno šalje. */
    const collectTables = () =>
        tables.length
            ? tables.map((t, i) => (i === activeTable ? { ...t, ...table } : t))
            : [];

    const resetMfContext = () => {
        setStep(1);
        setPkey(null);
        setFile(null);
        setPlugin(null);
        setConfig({});
        setSelectedPermission('active');
        setExist(true);
        setSelectedComposition('');
        setStructs([]);
        setTable({ fields: [], indexes: [], description: '' });
        setTables([]);
        setActiveTable(0);
        setDegradations([]);
        setExisting([]);
        setInstance('');
    };

    return (
        <MfImportContext.Provider
            value={{
                step,
                setStep,
                pkey,
                setPkey,
                file,
                fileType,
                setFile,
                plugin,
                setPlugin,
                config,
                setConfig,
                selectedPermission,
                setSelectedPermission,
                exist,
                setExist,
                selectedComposition,
                setSelectedComposition,
                structs,
                setStructs,
                table,
                setTable,
                tables,
                setTables,
                activeTable,
                selectTable,
                collectTables,
                degradations,
                existing,
                setExisting,
                setDegradations,
                instance,
                setInstance,
                resetMfContext,
                allCompositionsList,
                derivedName,
                setDerivedName
            }}
        >
            {children}
        </MfImportContext.Provider>
    );
};

export const useMfImport = () => useContext(MfImportContext);
